import { formatUnits } from 'viem';
import { formatNumber } from './formatNumber';

/**
 * Format a raw on-chain amount (smallest units, e.g. "217220958") into the
 * human-readable token amount using its decimals (e.g. "217.22096"). Use this for
 * any amount the API returns in smallest units. Falls back to the raw value if it
 * cannot be parsed.
 */
export const formatTokenAmount = (
  raw: string | number | bigint | null | undefined,
  decimals: number | null | undefined
): string => {
  if (raw === null || raw === undefined || raw === '') return '0';
  try {
    const str = raw.toString().trim();
    const negative = str.startsWith('-');
    // smallest-unit values are integers; drop any sign/stray fractional part
    const intPart = (negative ? str.slice(1) : str).split('.')[0] || '0';
    const human = formatUnits(BigInt(intPart), decimals ?? 0);
    return `${negative ? '-' : ''}${formatNumber(human)}`;
  } catch {
    return String(raw);
  }
};

export const formatAmount = (
  amount: string | number | bigint | null | undefined
): string => {
  if (!amount && amount !== 0) return '0';

  try {
    if (typeof amount === 'bigint') {
      const absAmount = amount < BigInt(0) ? -amount : amount;
      return absAmount.toString();
    }

    const numAmount = Number(amount);
    return Math.abs(numAmount).toString();
  } catch {
    return 'Invalid amount';
  }
};

export const formatAmountWithSign = (
  amount: string | number | bigint | null | undefined
): string => {
  if (!amount && amount !== 0) return '0';

  try {
    if (typeof amount === 'bigint') {
      return amount.toString();
    }

    const numAmount = Number(amount);
    return numAmount.toString();
  } catch {
    return 'Invalid amount';
  }
};

export const isDebitAmount = (
  amount: string | number | bigint | null | undefined
): boolean => {
  if (!amount && amount !== 0) return false;

  if (typeof amount === 'bigint') {
    return amount < BigInt(0);
  }

  const numAmount = Number(amount);
  return !isNaN(numAmount) && numAmount < 0;
};
